/**
 * Derived `data-testid` helpers — TASK 7.2 Step 1.
 *
 * The mock carries ZERO testids (ui-contract §3 key note); the contract's
 * candidates are DERIVED as "kebab-case of the accessible name", and
 * `packages/shared/src/testids.ts` promotes the named ones to constants and
 * exposes {@link toTestIdSlug} for the rest.
 *
 * Shell/nav/overlay controls that ui-contract §3 names get a TESTIDS constant
 * (imported directly at the call site). Controls the crawl only reached
 * through an overlay — the MORE-menu entries, the action-sheet rows, the
 * modal Cancel/confirm buttons, the full-player transport — are not enumerated
 * there, so they are derived here with the contract's OWN rule rather than
 * hand-written string literals. Nothing in this repo hardcodes a testid.
 *
 * @module
 */

import { toTestIdSlug } from '@streamymusic/shared';

/**
 * `data-testid` for a control identified by its accessible name, following the
 * contract's `<slug>-button` shape (e.g. "Close player" -> `close-player-button`).
 *
 * @param {string} accessibleName
 * @returns {string}
 */
export function controlTestId(accessibleName) {
  return `${toTestIdSlug(accessibleName)}-button`;
}

/**
 * `data-testid` for a control rendered as a link (`<a>`), matching the
 * contract's `<slug>-link` shape (e.g. `all-artists-link`).
 *
 * @param {string} accessibleName
 * @returns {string}
 */
export function linkTestId(accessibleName) {
  return `${toTestIdSlug(accessibleName)}-link`;
}

/**
 * `data-testid` for a two-state TOGGLE control, following the same
 * derive-from-the-name rule as {@link controlTestId} but with the `-toggle`
 * shape (e.g. "Lyrics" -> `lyrics-toggle`). A toggle keeps ONE stable id across
 * both states — its accessible name flips ("Show lyrics" / "Show artwork") but
 * the selector a test resolves it by must not.
 *
 * @param {string} accessibleName the STABLE name of the thing toggled, not the
 *   current-state verb (pass "Lyrics", never "Show lyrics")
 * @returns {string}
 */
export function toggleTestId(accessibleName) {
  return `${toTestIdSlug(accessibleName)}-toggle`;
}
